/**
 * Kiwi size chart links inside the Upcart cart drawer. v6.1 (PDP-safe).
 * - On pages with Kiwi containers, ZERO background work until the page's own
 *   chart has visibly rendered, or provably is not coming.
 * - Any element Kiwi auto-places during a background load is pre-hidden via
 *   html.ks-loading-window (12s watchdog), then removed. No visible flashes.
 * - Background loads run only while the user is idle.
 * - Caches handle->chart knowledge in localStorage (7d / 24h negative).
 */
(function () {
  'use strict';

  var LINK_CLASS = 'cart-ks-link';
  var DONE_ATTR = 'data-ks-done';
  var STORE_KEY = 'ksCartCharts';
  var POS_TTL = 7 * 24 * 3600 * 1000;
  var NEG_TTL = 24 * 3600 * 1000;
  var SURFACE_SELECTOR = '.ks-chart-container, .ks-calculator-container, .sizing-chart-container, [class*="ks-chart-float"]';

  // Garment products to pre-warm, in priority order (cart upsells first).
  var WARM_HANDLES = ['bmlcart', 'ppl2', 'leggings', 'bnsb', 'bnlb'];

  var pending = {};
  var chain = Promise.resolve();
  var settledPromise = null;
  var lastInteraction = 0;
  var windowActive = false;

  ['pointerdown', 'change', 'keydown', 'touchstart'].forEach(function (ev) {
    document.addEventListener(ev, function () { lastInteraction = Date.now(); }, true);
  });

  // Mark all currently visible/legit Kiwi surfaces so the window-scoped hide
  // rule never touches them. Called at boot, on ticks outside load windows,
  // and after each load window ends.
  function markKeep() {
    Array.prototype.forEach.call(document.querySelectorAll(SURFACE_SELECTOR), function (el) {
      el.setAttribute('data-ks-keep', '1');
    });
  }

  function readStore() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}'); } catch (e) { return {}; }
  }
  function writeStore(s) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch (e) {}
  }
  function getCached(handle) {
    var v = readStore()[handle];
    if (!v) return undefined;
    if (Date.now() - v.t > (v.sel ? POS_TTL : NEG_TTL)) return undefined;
    return v.sel;
  }
  function setCached(handle, sel) {
    var s = readStore();
    s[handle] = { sel: sel, t: Date.now() };
    writeStore(s);
  }

  function kiwiReady() {
    return typeof window.ks !== 'undefined' && typeof window.ks.loadSizing === 'function';
  }
  function waitKiwi() {
    return new Promise(function (resolve) {
      if (kiwiReady()) return resolve(true);
      var waited = 0;
      var iv = setInterval(function () {
        waited += 100;
        if (kiwiReady()) { clearInterval(iv); resolve(true); }
        else if (waited >= 60000) { clearInterval(iv); resolve(false); }
      }, 100);
    });
  }

  function containerHasContent() {
    return Array.prototype.some.call(
      document.querySelectorAll('#KiwiSizingChart'),
      function (d) { return d.innerHTML.length > 50; }
    );
  }

  // THE PDP GUARD. On pages with Kiwi containers, wait until the page's own
  // chart link has rendered, or until it clearly is not coming (settled flag
  // plus 8s of empty containers). Hard cap 25s.
  function waitPageSettled() {
    if (settledPromise) return settledPromise;
    settledPromise = waitKiwi().then(function (ok) {
      if (!ok) return false;
      var hasContainers = document.querySelectorAll('#KiwiSizingChart').length > 0;
      return new Promise(function (resolve) {
        var start = Date.now();
        var settledAt = null;
        var iv = setInterval(function () {
          var now = Date.now();
          if (window._ks_sizingLoaded === true && settledAt === null) settledAt = now;
          var done = false;
          if (!hasContainers) {
            done = settledAt !== null && (now - settledAt) >= 500;
          } else if (containerHasContent()) {
            done = true;
          } else if (settledAt !== null && (now - settledAt) >= 8000) {
            done = true;
          }
          if (done || (now - start) >= 25000) {
            clearInterval(iv);
            setTimeout(function () { resolve(true); }, 500);
          }
        }, 250);
      });
    });
    return settledPromise;
  }

  function waitIdle() {
    return new Promise(function (resolve) {
      var start = Date.now();
      (function check() {
        if (Date.now() - lastInteraction > 1500 || Date.now() - start > 10000) return resolve();
        setTimeout(check, 300);
      })();
    });
  }

  function chartIds() {
    return Array.prototype.map.call(
      document.querySelectorAll('[id^="ks-chart-content-"]'),
      function (d) { return d.id; }
    );
  }

  function surfaceSet() {
    var set = [];
    Array.prototype.forEach.call(document.querySelectorAll(SURFACE_SELECTOR), function (el) {
      set.push(el);
    });
    return set;
  }

  function kiwiLoad(handle, productJsonPromise) {
    var run = chain.then(function () {
      return waitPageSettled().then(function (ok) {
        if (!ok) return null;
        return waitIdle().then(function () {
          return productJsonPromise.then(function (j) {
            if (!j) return null;

            markKeep();
            windowActive = true;
            document.documentElement.classList.add('ks-loading-window');
            var watchdog = setTimeout(function () {
              windowActive = false;
              document.documentElement.classList.remove('ks-loading-window');
            }, 12000);

            var paused = [];
            Array.prototype.forEach.call(document.querySelectorAll('#KiwiSizingChart'), function (el) {
              paused.push({ el: el, html: el.innerHTML });
              el.id = 'KiwiSizingChart-ks-paused';
            });
            var beforeSurfaces = surfaceSet();
            var before = chartIds();

            window.ks.loadSizing({
              productData: {
                collections: '',
                tags: (j.tags || []).join(','),
                product: String(j.id),
                vendor: j.vendor || '',
                type: j.type || '',
                title: j.title || '',
                images: [], options: [], variants: []
              }
            });

            return new Promise(function (resolve) {
              var waited = 0;
              var iv = setInterval(function () {
                waited += 100;
                var fresh = chartIds().filter(function (id) { return before.indexOf(id) === -1; });
                if (fresh.length || waited >= 8000) {
                  clearInterval(iv);
                  setTimeout(function () {
                    Array.prototype.forEach.call(document.querySelectorAll(SURFACE_SELECTOR), function (el) {
                      if (beforeSurfaces.indexOf(el) !== -1) return;
                      if (el.closest('.kiwi-sizing-modal') || el.closest('.kiwi-sizing-modal-hidden-container')) return;
                      el.remove();
                    });
                    paused.forEach(function (p) {
                      p.el.id = 'KiwiSizingChart';
                      if (p.el.innerHTML !== p.html) p.el.innerHTML = p.html;
                    });
                    clearTimeout(watchdog);
                    windowActive = false;
                    document.documentElement.classList.remove('ks-loading-window');
                    markKeep();
                    resolve(fresh.length ? '#' + fresh[0] : null);
                  }, 400);
                }
              }, 100);
            });
          });
        });
      });
    });
    chain = run.then(function () {}, function () {});
    return run;
  }

  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function resolveChart(handle) {
    if (pending[handle]) return pending[handle];

    var cached = getCached(handle);
    if (cached === null) return Promise.resolve(null);

    if (typeof cached === 'string') {
      if (document.querySelector(cached)) return Promise.resolve(cached);
      var p1 = kiwiLoad(handle, fetchProduct(handle)).then(function (sel) {
        setCached(handle, sel);
        return sel;
      });
      pending[handle] = p1;
      p1.then(function () { delete pending[handle]; });
      return Promise.resolve(cached);
    }

    var p2 = kiwiLoad(handle, fetchProduct(handle)).then(function (sel) {
      setCached(handle, sel);
      return sel;
    });
    pending[handle] = p2;
    p2.then(function () { delete pending[handle]; });
    return p2;
  }

  function injectLink(titleLink, chartSel, handle) {
    if (!chartSel) return;
    var row = titleLink.parentElement;
    if (!row || row.querySelector('.' + LINK_CLASS)) return;
    var a = document.createElement('a');
    a.href = 'javascript:void(0)';
    a.className = LINK_CLASS;
    a.setAttribute('data-ks-chart', chartSel);
    a.setAttribute('data-ks-handle', handle);
    a.textContent = 'Size chart';
    titleLink.insertAdjacentElement('afterend', a);
  }

  function scan() {
    var cart = document.getElementById('CartPopup');
    if (!cart) return;

    var links = cart.querySelectorAll(
      'a.upcart-internal-component-product-tile__title-link[href*="/products/"],' +
      'a.upcart-internal-component-upsell-tile__title-link[href*="/products/"]'
    );

    Array.prototype.forEach.call(links, function (titleLink) {
      if (titleLink.getAttribute(DONE_ATTR)) return;
      titleLink.setAttribute(DONE_ATTR, '1');
      var m = (titleLink.getAttribute('href') || '').match(/\/products\/([a-z0-9-]+)/i);
      if (!m) return;
      var handle = m[1];
      resolveChart(handle).then(function (sel) {
        var current = document.getElementById('CartPopup');
        if (!current || !sel) return;
        var fresh = current.querySelectorAll('a[href*="/products/' + handle + '"]');
        Array.prototype.forEach.call(fresh, function (l) {
          if (l.className && /title-link/.test(l.className)) injectLink(l, sel, handle);
        });
      });
    });
  }

  document.addEventListener('click', function (e) {
    if (!e.target || typeof e.target.closest !== 'function') return;
    var a = e.target.closest('.' + LINK_CLASS);
    if (!a) return;
    e.preventDefault();
    e.stopPropagation();
    var sel = a.getAttribute('data-ks-chart');
    var handle = a.getAttribute('data-ks-handle');

    function openIfReady() {
      if (sel && window._ks_modal && document.querySelector(sel)) {
        window._ks_modal.open(sel);
        return true;
      }
      return false;
    }
    if (openIfReady()) return;

    var waiter = pending[handle] || resolveChart(handle);
    waiter.then(function (freshSel) {
      if (freshSel && freshSel !== sel) { sel = freshSel; a.setAttribute('data-ks-chart', freshSel); }
      openIfReady();
    });
  }, true);

  var mo = new MutationObserver(function () {
    if (document.getElementById('CartPopup')) scan();
  });

  function start() {
    markKeep();
    mo.observe(document.body, { childList: true, subtree: true });
    scan();
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (!windowActive) markKeep();
      scan();
      if (tries >= 30) clearInterval(iv);
    }, 1000);

    setTimeout(function () {
      WARM_HANDLES.forEach(function (h) {
        if (getCached(h) === undefined) resolveChart(h);
      });
    }, 1000);
  }

  // Start when the cart drawer first opens, or once the page has loaded and gone idle,
  // instead of at DOMContentLoaded: keeps the page's first seconds free for the product page itself.
  var started = false;
  function startOnce() {
    if (started) return;
    started = true;
    start();
  }
  function hookCartOpen() {
    if (typeof window.upcartSubscribeCartOpened !== 'function') return false;
    window.upcartSubscribeCartOpened(startOnce);
    return true;
  }
  if (!hookCartOpen()) {
    // Upcart loads after the page's first interaction; keep checking for its hook for up to 30 s
    var hookTries = 0;
    var hookIv = setInterval(function () {
      if (started || hookCartOpen() || ++hookTries >= 60) clearInterval(hookIv);
    }, 500);
  }
  function startWhenIdle() {
    setTimeout(function () {
      if ('requestIdleCallback' in window) window.requestIdleCallback(startOnce, { timeout: 2000 });
      else startOnce();
    }, 3000);
  }
  if (document.readyState === 'complete') startWhenIdle();
  else window.addEventListener('load', startWhenIdle);
})();